import { THEMES, type ThemeChoice } from "@aybuben/client/device/theme-choice.ts";
import type { SyncStatus } from "@aybuben/client/progress/store.ts";
import { ENDONYMS, LANGS, type LangId } from "@aybuben/core/lang.ts";
import { LAYOUTS, type LayoutId } from "@aybuben/core/writing/layouts.ts";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";

import { errorKey } from "../auth/copy.ts";
import { useStrings, type UiKey } from "../i18n/index.tsx";
import { NO_CAPTURE } from "../posthog.ts";
import { COL, FOCUS } from "../theme.ts";
import { Btn } from "./primitives.tsx";

export interface AccountBodyProps {
	email: string;
	sync: SyncStatus;
	onSignOut: () => Promise<void>;
	onDelete: () => Promise<void>;
	/** Rendered between the account details and the sign-out/delete actions. */
	children?: ReactNode;
	/** A guest has no email to show and nothing to sign out of: saving is the action. */
	guest?: boolean;
	onSave?: () => void;
	onLeaveGuest?: () => Promise<void>;
}

/**
 * The account surface, deliberately NOT reachable from the lesson loop.
 *
 * The sync line is the only place a learner ever hears about syncing at all:
 * "dirty" is ordinary and says so quietly, "stale" is the stale-version refusal and asks
 * for a reload. "pushing" and "idle" say nothing — a progress indicator for a
 * background push is noise, and a failed sync is not a failed lesson.
 */
export function AccountBody({
	email,
	sync,
	onSignOut,
	onDelete,
	children,
	guest,
	onSave,
	onLeaveGuest,
}: AccountBodyProps) {
	const t = useStrings();
	const navigate = useNavigate();
	const [asking, setAsking] = useState(false);
	const [busy, setBusy] = useState(false);
	const [failed, setFailed] = useState(false);
	const [signingOut, setSigningOut] = useState(false);
	const [signOutErr, setSignOutErr] = useState<UiKey | null>(null);
	const [leaving, setLeaving] = useState(false);
	const [leaveBusy, setLeaveBusy] = useState(false);
	const [leaveErr, setLeaveErr] = useState<UiKey | null>(null);
	// Same shape as signOut below: a failure (almost always offline) keeps the
	// guest where they are and says why.
	const leave = () => {
		if (leaveBusy || !onLeaveGuest) return;
		setLeaveBusy(true);
		setLeaveErr(null);
		onLeaveGuest()
			.catch((e: unknown) => setLeaveErr(errorKey(e)))
			.finally(() => setLeaveBusy(false));
	};
	const note: UiKey | null =
		sync === "dirty" ? "sync.dirty" : sync === "stale" ? "sync.stale" : null;

	// Not useSubmit: this is a confirmation, not a form, and `Btn` renders a
	// bare <button> with no `type`, so a Btn inside a <form> submits it.
	const remove = () => {
		if (busy) return;
		setBusy(true);
		setFailed(false);
		onDelete()
			.catch(() => setFailed(true))
			.finally(() => setBusy(false));
	};

	// Root only forgets the remembered session and clears the phase on
	// success; a rejection here is almost always "offline", and the learner's
	// own cached progress must stay reachable, so this is where the error —
	// the real one, not a fixed sentence — has to surface.
	const signOut = () => {
		if (signingOut) return;
		setSigningOut(true);
		setSignOutErr(null);
		onSignOut()
			.then(() => navigate("/auth"))
			.catch((e: unknown) => setSignOutErr(errorKey(e)))
			.finally(() => setSigningOut(false));
	};

	if (guest)
		return (
			<>
				<p className="mt-2 text-sm leading-relaxed" style={{ color: COL.mute }}>
					{t("account.guest")}
				</p>
				{children}
				{leaving ? (
					<div className="mt-6">
						<div className="text-lg font-extrabold">{t("auth.leaveTitle")}</div>
						<p className="mt-2 text-sm" style={{ color: COL.mute }}>
							{t("auth.leaveBody")}
						</p>
						{leaveErr && (
							<p className="mt-3 text-sm font-semibold" role="alert" style={{ color: COL.red }}>
								{t(leaveErr)}
							</p>
						)}
						<div className="mt-5 flex flex-col gap-3">
							<Btn onClick={() => setLeaving(false)}>{t("auth.back")}</Btn>
							<button
								onClick={leave}
								disabled={leaveBusy}
								className={`rounded-lg py-2 font-semibold ${FOCUS}`}
								style={{ color: COL.red }}
							>
								{leaveBusy ? t("auth.busy") : t("auth.leaveConfirm")}
							</button>
						</div>
					</div>
				) : (
					<div className="mt-6 flex flex-col gap-3">
						<Btn onClick={onSave} color={COL.pom}>
							{t("account.save")}
						</Btn>
						<button
							onClick={() => setLeaving(true)}
							className={`rounded-lg py-2 font-semibold ${FOCUS}`}
							style={{ color: COL.lapis }}
						>
							{t("account.haveAccount")}
						</button>
					</div>
				)}
			</>
		);

	return (
		<>
			<p className={`mt-2 text-sm ${NO_CAPTURE}`} style={{ color: COL.mute }}>
				{t("account.signedInAs", { email })}
			</p>
			{note && (
				<p className="mt-3 text-sm font-semibold" style={{ color: COL.mute }}>
					{t(note)}
				</p>
			)}

			{children}

			{asking ? (
				<div className="mt-6">
					<div className="text-lg font-extrabold">{t("account.deleteTitle")}</div>
					<p className="mt-2 text-sm" style={{ color: COL.mute }}>
						{t("account.deleteBody")}
					</p>
					{failed && (
						<p className="mt-3 text-sm font-semibold" role="alert" style={{ color: COL.red }}>
							{t("account.deleteFailed")}
						</p>
					)}
					<div className="mt-5 flex flex-col gap-3">
						<Btn onClick={() => setAsking(false)}>{t("account.deleteKeep")}</Btn>
						<button
							disabled={busy}
							onClick={remove}
							className={`rounded-lg py-2 font-semibold ${FOCUS}`}
							style={{ color: COL.red }}
						>
							{busy ? t("auth.busy") : t("account.deleteConfirm")}
						</button>
					</div>
				</div>
			) : (
				<div className="mt-6 flex flex-col gap-3">
					{signOutErr && (
						<p className="text-sm font-semibold" role="alert" style={{ color: COL.red }}>
							{t(signOutErr)}
						</p>
					)}
					<Btn onClick={signOut} disabled={signingOut}>
						{signingOut ? t("auth.busy") : t("account.signOut")}
					</Btn>
					<button
						onClick={() => setAsking(true)}
						className={`rounded-lg py-2 font-semibold ${FOCUS}`}
						style={{ color: COL.red }}
					>
						{t("account.delete")}
					</button>
				</div>
			)}
		</>
	);
}

interface LanguagePickerProps {
	lang: LangId;
	onPick: (l: LangId) => void;
}

/** Every language as its own button, so the options are visible before a tap. */
export function LanguagePicker({ lang, onPick }: LanguagePickerProps) {
	return (
		<div className="mt-2 flex gap-2">
			{LANGS.map((l) => (
				<button
					key={l}
					onClick={() => onPick(l)}
					aria-pressed={lang === l}
					className={`flex-1 rounded-2xl py-2 font-extrabold ${FOCUS}`}
					style={{
						background: lang === l ? COL.pomSoft : COL.card,
						color: lang === l ? COL.pom : COL.mute,
						border: `2px solid ${lang === l ? COL.pom : COL.line}`,
					}}
				>
					{ENDONYMS[l]}
				</button>
			))}
		</div>
	);
}

const LAYOUT_NAME: Record<LayoutId, UiKey> = {
	phonetic: "account.layoutPhonetic",
	standard: "account.layoutStandard",
};

interface LayoutPickerProps {
	layout: LayoutId;
	onPick: (l: LayoutId) => void;
}

/** The in-app keyboard's layout, laid out like the language picker. */
export function LayoutPicker({ layout, onPick }: LayoutPickerProps) {
	const t = useStrings();
	return (
		<div className="mt-2 flex gap-2">
			{LAYOUTS.map((l) => (
				<button
					key={l}
					onClick={() => onPick(l)}
					aria-pressed={layout === l}
					className={`flex-1 rounded-2xl py-2 font-extrabold ${FOCUS}`}
					style={{
						background: layout === l ? COL.pomSoft : COL.card,
						color: layout === l ? COL.pom : COL.mute,
						border: `2px solid ${layout === l ? COL.pom : COL.line}`,
					}}
				>
					{t(LAYOUT_NAME[l])}
				</button>
			))}
		</div>
	);
}

const THEME_NAME: Record<ThemeChoice, UiKey> = {
	system: "account.themeSystem",
	light: "account.themeLight",
	dark: "account.themeDark",
};

/** Light, dark, or as the device says; laid out like the language picker. */
export function ThemePicker({
	theme,
	onPick,
}: {
	theme: ThemeChoice;
	onPick: (t: ThemeChoice) => void;
}) {
	const t = useStrings();
	return (
		<div className="mt-2 flex gap-2">
			{THEMES.map((th) => (
				<button
					key={th}
					onClick={() => onPick(th)}
					aria-pressed={theme === th}
					className={`flex-1 rounded-2xl py-2 font-extrabold ${FOCUS}`}
					style={{
						background: theme === th ? COL.pomSoft : COL.card,
						color: theme === th ? COL.pom : COL.mute,
						border: `2px solid ${theme === th ? COL.pom : COL.line}`,
					}}
				>
					{t(THEME_NAME[th])}
				</button>
			))}
		</div>
	);
}
